/* =========================================================================
   Routinae — Routine Finder: interface layer.

   Owns the DOM, accessibility, state and rendering. Knows nothing about
   dermatology — that is routina-finder-engine.js — and nothing about
   wording, which arrives as a translated dictionary from section settings.

   Accessibility decisions worth keeping:
     - Real <fieldset>/<legend> and real radio/checkbox inputs. Arrow-key
       grouping, announcement and focus come free from the platform; a div
       with role="radio" would have to reimplement all three, worse.
     - Auto-advance fires on POINTER selection only. Arrowing through radios
       changes the selection on every keypress, so auto-advancing on `change`
       would eject a keyboard user from the question at the first arrow press.
     - Progress is announced through a polite live region, not inferred from
       a visual bar.
     - Every step change moves focus to the new legend, so a screen-reader
       user lands on the question rather than at the top of the document.
     - The supporting line is wired to the group with aria-describedby, so it
       is read once with the question rather than skipped or read per option.
     - prefers-reduced-motion suppresses the auto-advance delay as well as the
       CSS transitions: the delay exists to let a selection register visually,
       and a user who has asked for no motion does not need it.

   State: sessionStorage only, and only for a quiz the visitor started. It is
   cleared when the tab closes. Nothing persistent is written unless the
   visitor asks for it. See ai/README.md for the ePrivacy reasoning.

   Placeholder convention: %name%, NOT {{ name }}. Shopify's theme validator
   reads {{ }} inside a setting VALUE as a dynamic source binding, and
   `product` is a real resource, so a default of "Remove {{ product }}" makes
   page.routine.json unpushable with "has a dynamic source with resource
   'product' which is incompatible with the current template of type 'page'".
   Never reintroduce {{ }} into a setting default.
   ========================================================================= */

(function () {
  'use strict';

  var E = window.RoutinaEngine;

  var REDUCED = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* helpers                                                             */
  /* ------------------------------------------------------------------ */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* Copy is plain text. Blank lines become paragraphs. No innerHTML path
     exists for merchant, visitor or model strings anywhere in this file —
     which is also what makes the AI layer safe to render: generated text can
     only ever become a text node. */
  function paras(parent, text, cls) {
    String(text || '').split(/\n\s*\n/).forEach(function (chunk) {
      var t = chunk.trim();
      if (t) parent.appendChild(el('p', cls || null, t));
    });
  }

  function fill(tpl, vars) {
    return String(tpl || '').replace(/%(\w+)%/g, function (m, k) {
      return vars[k] != null ? vars[k] : '';
    });
  }

  /* ------------------------------------------------------------------ */
  /* Output guard on copy we did not write in a template.                */
  /*                                                                     */
  /* ai/shared.js has a `violates()` filter that every generated sentence */
  /* passes through before it reaches a page. It has always had a hole:   */
  /* it guards the model and nothing else, while the result page also     */
  /* renders `custom.best_for` and `custom.skip_if` straight off the      */
  /* product record, in the shop's own voice, directly underneath a       */
  /* product we are recommending.                                         */
  /*                                                                     */
  /* Those metafields are edited in the Shopify admin, by a human, with   */
  /* no review step and no test between the edit and the live page. The   */
  /* live catalogue already contains one: a body lotion whose "best for"  */
  /* line names a skin condition. Under Directive 2001/83/EC Art. 1(2)(a) */
  /* a medicinal claim can be made "by presentation" — wording alone is   */
  /* enough, no active ingredient required — so a condition name in a     */
  /* "this is what this product is for" line is precisely the sentence    */
  /* the whole engine is built to avoid, arriving through the one door    */
  /* nobody was watching.                                                 */
  /*                                                                     */
  /* This is deliberately a SUPPRESSION and not a rewrite. We do not know */
  /* what the merchant meant, we cannot repair a sentence in four         */
  /* languages from the browser, and a half-cleaned claim is worse than a */
  /* missing line. The product still appears, with its price and its      */
  /* link; only the unverifiable sentence is dropped. Failing closed      */
  /* costs a line of supporting copy. Failing open costs the shop its     */
  /* cosmetic classification.                                              */
  /*                                                                     */
  /* Keep this list in sync with BANNED in ai/shared.js. It is the same   */
  /* boundary, applied to the other input.                                */
  /* ------------------------------------------------------------------ */

  var CLAIM_GUARD = [
    // Condition names.
    /\bacne\b/i, /\beczema\b/i, /\bdermatitis\b/i, /\bpsoriasis\b/i,
    /\brosacea\b/i, /\bdandruff\b/i, /\bkeratosis\b/i, /\bmelasma\b/i,
    /\bseborrh\w*/i, /\bfungal\b/i, /\binfections?\b/i, /\blesions?\b/i,
    // Therapeutic verbs.
    /\b(treats?|treating|cures?|curing|heals?|healing)\b/i,
    /\bdiagnos\w*/i,
    /\b(repairs?|restores?)\s+(the\s+|your\s+)?(skin|barrier)\b/i,
    // Claims needing per-SKU substantiation the responsible person holds.
    /\bhypoallergenic\b/i, /\bnon[- ]comedogenic\b/i,
    /\bdermatologist[- ]?(approved|tested|recommended)\b/i,
    /\bclinically proven\b/i, /\bproven to\b/i, /\bguarantee[ds]?\b/i,
    // "Free from" claims. We hold no formulation data for any SKU.
    /\b(fragrance|perfume|alcohol|paraben|sulphate|sulfate|chemical)[- ]free\b/i,
    // Denigration of an ingredient class.
    /\b(toxic|harmful|nasty)\b/i
  ];

  /* Localisation note: this list is English, and the metafields it guards
     are English — `dump-translatable.ps1` excludes METAFIELD from the
     translation workflow on purpose, because that is where INCI lives. If
     these strings are ever translated, this guard must be translated with
     them or it silently stops guarding. */
  function claimSafe(text) {
    var t = String(text || '');
    if (!t) return '';
    for (var i = 0; i < CLAIM_GUARD.length; i++) {
      if (CLAIM_GUARD[i].test(t)) return '';
    }
    return t;
  }

  /* Shopify names the sole variant of a single-option product "Default
     Title". It is an internal placeholder and must never reach a page. */
  function variantLabel(p) {
    var v = String(p && p.vn || '').trim();
    if (!v || v === 'Default Title') return '';
    return v;
  }

  function money(cents, format) {
    var v = (cents || 0) / 100;
    var f = format || '€{{amount}}';
    function amount(dec, thou) {
      var s = v.toFixed(2).split('.');
      var whole = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return whole + dec + s[1];
    }
    return f
      .replace(/\{\{\s*amount\s*\}\}/g, amount('.', ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount(',', '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(v)))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(v)))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amount('.', "'"));
  }

  /* ------------------------------------------------------------------ */
  /* cart                                                                */
  /*                                                                     */
  /* Shopify's AJAX cart, driven the way Dawn's own product form drives   */
  /* it, so a routine added from here lands in exactly the same place as  */
  /* a product added from a product page and the drawer updates rather    */
  /* than going stale.                                                    */
  /*                                                                     */
  /* Verified against the live shop rather than assumed, because three of */
  /* these behaviours are not what the documentation implies:             */
  /*                                                                     */
  /*   - `/cart/add.js` accepts an `items` ARRAY, so a whole routine is   */
  /*     one request and one atomic outcome. Looping the single-item form */
  /*     would leave a half-added cart on the first failure.               */
  /*   - a POST with no body returns HTTP 411 Length Required, not 400,   */
  /*     and not the error JSON. Hence `{}` rather than an empty body on  */
  /*     every call here.                                                  */
  /*   - a bad variant returns HTTP 422 with a JSON body carrying         */
  /*     `description` ("Cannot find variant") and no `errors` key. A     */
  /*     handler that only reads `errors` reports success on a failed add.*/
  /*                                                                     */
  /* Everything shown to the visitor on failure is a translated setting.  */
  /* Shopify's own `description` is English-only and is not surfaced.     */
  /* ------------------------------------------------------------------ */

  function cartHost() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function cartAddUrl() {
    var base = (window.routes && window.routes.cart_add_url) || '/cart/add';
    return base + '.js';
  }

  /* Adds one or more variants in a single request.

     Resolves with the parsed response on success. Rejects with an Error
     whose `.code` is 'invalid' when Shopify refused the line (sold out,
     unknown variant — a fact about the product) and 'network' when the
     request never completed (a fact about the connection). The two want
     different sentences and the caller has both. */
  function cartAdd(lines) {
    if (!window.fetch) {
      return Promise.reject(Object.assign(new Error('no fetch'), { code: 'network' }));
    }

    var body = { items: lines.map(function (l) { return { id: l.id, quantity: l.quantity || 1 }; }) };

    // Ask for the drawer markup in the same round trip. Without this,
    // `renderContents` throws on `parsedState.sections[...]` and the cart is
    // correct on the server while the page says nothing happened.
    var host = cartHost();
    if (host && typeof host.getSectionsToRender === 'function') {
      body.sections = host.getSectionsToRender().map(function (s) { return s.id; }).join(',');
      body.sections_url = window.location.pathname;
    }

    return fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    })
      .then(function (r) {
        return r.json().then(
          function (data) { return { ok: r.ok, data: data }; },
          function () { return { ok: false, data: null }; }
        );
      })
      .then(function (res) {
        // Shopify signals a refusal with a `status` field in a 2xx-shaped
        // body as well as with a non-2xx code. Treat either as a refusal.
        if (!res.ok || !res.data || res.data.status) {
          throw Object.assign(new Error('cart refused'), { code: 'invalid', payload: res.data });
        }
        return res.data;
      })
      .catch(function (e) {
        if (e && e.code) throw e;
        throw Object.assign(new Error('cart unreachable'), { code: 'network' });
      });
  }

  /* Paint the result of a successful add. Falls back to a full navigation
     when the theme has no drawer to update — the add already happened, so
     the worst case is a page load, never a lost cart. */
  function cartReveal(data, trigger) {
    var host = cartHost();
    if (host && data && data.sections && typeof host.renderContents === 'function') {
      if (typeof host.setActiveElement === 'function' && trigger) host.setActiveElement(trigger);
      if (host.classList.contains('is-empty')) host.classList.remove('is-empty');
      host.renderContents(data);
      return true;
    }
    window.location = (window.routes && window.routes.cart_url) || '/cart';
    return false;
  }

  /* Compact, human-unreadable-but-stable share encoding. Order follows
     RoutinaEngine.QUESTIONS so a link stays valid across copy edits. The
     parameter name carries a version (rf2, August 2026): the adaptive
     follow-up questions changed the index mapping, so a pre-versioned link
     must fall back to a fresh quiz rather than decode into wrong answers.

     Free-text answers are deliberately NOT encoded. A shareable URL carrying
     what someone wrote about their skin is a disclosure waiting to happen,
     and the recipient of a shared link has no interest in it anyway. */
  function shareable(q) { return q.type !== 'text' && q.values && q.values.length; }

  function encode(answers) {
    return E.QUESTIONS.map(function (q) {
      if (!shareable(q)) return '';
      var v = answers[q.key];
      if (v == null) return '';
      if (Array.isArray(v)) return v.map(function (x) { return q.values.indexOf(x); }).join('.');
      return String(q.values.indexOf(v));
    }).join('-');
  }

  function decode(str) {
    var parts = String(str || '').split('-'), out = {};
    E.QUESTIONS.forEach(function (q, i) {
      if (!shareable(q)) return;
      var raw = parts[i];
      if (raw == null || raw === '') return;
      if (q.type === 'multi') {
        out[q.key] = raw.split('.').map(function (n) { return q.values[+n]; })
                        .filter(Boolean);
      } else {
        var v = q.values[+raw];
        if (v) out[q.key] = v;
      }
    });
    return out;
  }

  /* ------------------------------------------------------------------ */
  /* controller                                                          */
  /* ------------------------------------------------------------------ */

  function Finder(root) {
    this.root = root;
    this.cfg = JSON.parse(root.querySelector('[data-rf-config]').textContent);
    this.copy = JSON.parse(root.querySelector('[data-rf-copy]').textContent);
    this.qcopy = JSON.parse(root.querySelector('[data-rf-questions]').textContent);
    this.cat = JSON.parse(root.querySelector('[data-rf-catalogue]').textContent);

    this.form = root.querySelector('[data-rf-form]');
    this.mount = root.querySelector('[data-rf-mount]');
    this.live = root.querySelector('[data-rf-live]');
    this.bar = root.querySelector('[data-rf-bar]');
    this.count = root.querySelector('[data-rf-count]');
    this.result = root.querySelector('[data-rf-result]');
    this.nav = root.querySelector('[data-rf-nav]');
    this.back = root.querySelector('[data-rf-back]');
    this.next = root.querySelector('[data-rf-next]');
    this.skip = root.querySelector('[data-rf-skip]');
    this.waitlist = root.querySelector('[data-rf-waitlist]');
    this.intro = root.querySelector('[data-rf-intro]');

    this.aiOn = !!(this.cfg.ai && this.cfg.ai.endpoint);
    this.policy = { unverifiedPolicy: this.cfg.unverified || 'caveat' };
    // Which departments we cannot serve at all today. Computed from the live
    // catalogue, so it self-corrects the moment a draft is published — there
    // is no list to maintain and nothing to forget to update.
    this.unstocked = E.unstockedTracks(this.cat);

    this.answers = {};
    this.index = 0;
    this.removed = {};
    this.aiApplied = [];
    this.aiWrote = false;

    this.restore();
    this.bind();
    this.render();
  }

  Finder.prototype.t = function (key) {
    return this.copy[key] != null ? this.copy[key] : '';
  };

  /* -- state ---------------------------------------------------------- */

  Finder.prototype.storeKey = function () { return 'routina-finder:v2:' + this.cfg.mode; };

  Finder.prototype.save = function () {
    try {
      // The free-text note is held in memory for the length of the session
      // only. It is the one answer that could contain anything at all, so it
      // is never written to storage and never put in the URL.
      var a = {}, k;
      for (k in this.answers) if (this.answers.hasOwnProperty(k) && k !== 'note') a[k] = this.answers[k];
      sessionStorage.setItem(this.storeKey(), JSON.stringify({ a: a, i: this.index }));
    } catch (e) { /* private mode — the tool still works, it just forgets */ }
  };

  Finder.prototype.restore = function () {
    var hash = window.location.hash.match(/[#&]rf2=([^&]+)/);
    if (hash) {
      this.answers = decode(decodeURIComponent(hash[1]));
      this.index = this.queue().length;
      return;
    }
    try {
      var raw = sessionStorage.getItem(this.storeKey());
      if (raw) {
        var s = JSON.parse(raw);
        this.answers = s.a || {};
        this.index = s.i || 0;
      }
    } catch (e) { /* ignore */ }
  };

  /* -- question flow -------------------------------------------------- */

  /* ------------------------------------------------------------------ *
     Can this question actually be answered on this page?

     THE BUG THIS EXISTS TO MAKE IMPOSSIBLE.

     The engine plans questions from the answer graph. The wording comes from
     section blocks, which a template supplies independently. Nothing checked
     that the two agreed, and when they did not the failure was silent and
     total: `render()` fell back to `qc.label || q.key`, so the legend printed
     the raw engine key — the word "sun" — and `renderOptions` skips any
     option whose text is blank, so it drew a fieldset with zero radios. A
     Continue button with nothing to advance past. A hard stop mid-quiz, on a
     live page, reported by the owner rather than by anything we had built.

     Blanking a question's label is the DOCUMENTED way for a merchant to
     shorten the finder — see the header of routina-finder-questions.liquid.
     The feature was specified and never implemented, so the same missing copy
     meant "remove this question" in the documentation and "trap the visitor"
     in the code.

     A question needs a legend and at least one option a person can choose.
     Anything less is not a shortened quiz, it is a dead end, and dropping the
     question is strictly better than presenting an unanswerable one.
     ------------------------------------------------------------------ */

  Finder.prototype.answerable = function (q) {
    var qc = this.qcopy[q.key];
    if (!qc || !String(qc.label || '').trim()) return false;
    if (q.type === 'text') return true;

    var n = 0;
    for (var i = 0; i < q.values.length; i++) {
      if (String(qc['opt' + (i + 1)] || '').trim()) n++;
    }
    // `min` is how many a multi-select needs before Continue will accept it.
    // Offering fewer options than that is the same dead end by arithmetic.
    return n >= Math.max(1, q.min || 1);
  };

  /* Everything the ENGINE wants to ask, before any question is checked for
     wording. One implementation, because the two callers below have to agree
     about it: if `queue()` and `unanswerable()` ever planned differently, a
     question could be silently dropped from the quiz and simultaneously not
     reported to the theme editor, which is the original bug with an extra
     step.

     A section may pin the tracks (e.g. the face-only read page). When it
     does, the routing question is answered for the visitor and dropped from
     the queue rather than being shown with one option.

     ORDER IS LOAD-BEARING. `answers.track` must be assigned BEFORE planning:
     every face, body, hair and scent question is gated on the track answer,
     so planning against an unset track returns only the two unconditional
     questions. Getting this backwards produces a queue that is wrong on the
     first call and right on every subsequent one, which is the kind of bug
     that hides behind a re-render. */
  Finder.prototype.planned = function () {
    var opts = { ai: this.aiOn };
    var pinned = this.cfg.tracks;
    if (pinned && pinned.length) {
      this.answers.track = pinned;
      return E.plannedQuestions(this.answers, opts).filter(function (q) { return q.key !== 'track'; });
    }
    return E.plannedQuestions(this.answers, opts);
  };

  /* What the visitor will actually be shown. */
  Finder.prototype.queue = function () {
    var self = this;
    return this.planned().filter(function (q) { return self.answerable(q); });
  };

  /* Everything the engine wanted to ask and this template cannot. Empty on a
     correctly configured page; the theme editor is told about it loudly, and
     a shopper is told nothing because there is nothing they can do. */
  Finder.prototype.unanswerable = function () {
    var self = this;
    return this.planned()
      .filter(function (q) { return !self.answerable(q); })
      .map(function (q) { return q.key; });
  };

  /* FAIL LOUDLY WHERE IT CAN BE FIXED.
     Silence is what let the missing questions ship. In the theme editor — and
     only there — the gap is drawn on the page, named, with the block type to
     add. On the storefront this renders nothing at all: a shopper cannot add a
     block, and a warning they cannot act on is just a broken-looking page. */
  Finder.prototype.warnEditor = function () {
    var inEditor = typeof window.Shopify !== 'undefined' && window.Shopify && window.Shopify.designMode;
    if (!inEditor) return;
    var missing = this.unanswerable();
    var box = this.root.querySelector('[data-rf-editor-warning]');
    if (box) { box.parentNode.removeChild(box); }
    if (!missing.length) return;

    var warn = el('div', 'rf-editor-warning');
    warn.setAttribute('data-rf-editor-warning', '1');
    warn.appendChild(el('strong', null, 'Routinae finder: missing question wording'));
    warn.appendChild(el('p', null,
      'The engine plans these questions for this page and no Question block supplies ' +
      'their wording, so they have been dropped from the quiz: ' + missing.join(', ') + '. ' +
      'Add a Question block for each, or leave them out on purpose. ' +
      'This notice is only visible in the theme editor.'));
    this.root.insertBefore(warn, this.root.firstChild);
  };

  Finder.prototype.bind = function () {
    var self = this;

    this.form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      self.advance();
    });

    this.back.addEventListener('click', function () {
      if (self.index > 0) { self.index--; self.render(); }
    });

    if (this.skip) {
      this.skip.addEventListener('click', function () { self.finish(); });
    }

    this.root.addEventListener('click', function (ev) {
      var again = ev.target.closest && ev.target.closest('[data-rf-restart]');
      if (again) { ev.preventDefault(); self.reset(); return; }

      var drop = ev.target.closest && ev.target.closest('[data-rf-remove]');
      if (drop) {
        ev.preventDefault();
        self.removed[drop.getAttribute('data-rf-remove')] = true;
        self.rerender(true);
        return;
      }

      var undo = ev.target.closest && ev.target.closest('[data-rf-undo]');
      if (undo) {
        ev.preventDefault();
        delete self.removed[undo.getAttribute('data-rf-undo')];
        self.rerender(true);
        return;
      }

      var one = ev.target.closest && ev.target.closest('[data-rf-add]');
      if (one) {
        ev.preventDefault();
        var vid = parseInt(one.getAttribute('data-rf-add'), 10);
        if (vid) self.runAdd([{ id: vid, quantity: 1 }], one);
        return;
      }

      var all = ev.target.closest && ev.target.closest('[data-rf-addall]');
      if (all) {
        ev.preventDefault();
        self.runAdd(self.addableLines(), all);
      }
    });

    if (this.waitlist) this.bindWaitlist();
  };

  Finder.prototype.render = function () {
    // Recomputed per question, not once at boot: which questions the engine
    // plans depends on the answers so far, so a gap can appear halfway
    // through — which is exactly where the live one appeared.
    this.warnEditor();

    var q = this.queue()[this.index];
    if (!q) { this.finish(); return; }

    var qc = this.qcopy[q.key] || {};
    var total = this.queue().length;
    var self = this;

    this.mount.textContent = '';

    var fs = el('fieldset', 'rf__fs');
    var lg = el('legend', 'rf__q', qc.label || q.key);
    lg.setAttribute('tabindex', '-1');
    fs.appendChild(lg);

    var describedBy = [];
    if (qc.help) {
      var help = el('p', 'rf__help', qc.help);
      help.id = 'rf-help-' + this.cfg.uid + '-' + q.key;
      describedBy.push(help.id);
      fs.appendChild(help);
    }
    if (q.type === 'multi') {
      var hint = el('p', 'rf__hint', this.t(q.optional ? 'ui_multi_optional' : 'ui_multi_hint'));
      hint.id = 'rf-hint-' + this.cfg.uid + '-' + q.key;
      describedBy.push(hint.id);
      fs.appendChild(hint);
    }

    if (q.type === 'text') {
      this.renderTextQuestion(fs, q, qc, describedBy);
    } else {
      this.renderOptions(fs, q, qc);
    }

    if (describedBy.length) fs.setAttribute('aria-describedby', describedBy.join(' '));
    this.mount.appendChild(fs);

    this.back.hidden = this.index === 0;
    this.next.hidden = false;
    this.next.textContent = this.t('ui_continue');
    if (this.skip) this.skip.hidden = !E.canResolve(this.answers);

    var pos = this.index + 1;
    var label = fill(this.t('ui_progress'), { current: pos, total: total });
    this.count.textContent = label;
    this.bar.style.width = ((this.index / total) * 100) + '%';
    this.bar.parentNode.setAttribute('aria-valuenow', String(pos));
    this.bar.parentNode.setAttribute('aria-valuemax', String(total));

    this.live.textContent = label + '. ' + (qc.label || '');
    this.form.hidden = false;
    this.result.hidden = true;
    // The promise panel belongs with the questions, not with the answer.
    // It comes back on "start again", which is when it is useful again.
    if (this.intro) this.intro.hidden = false;
    if (this.waitlist) this.waitlist.hidden = true;

    lg.focus({ preventScroll: true });
    this.save();
  };

  Finder.prototype.renderOptions = function (fs, q, qc) {
    var list = el('div', 'rf__opts');
    var current = this.answers[q.key];
    var self = this;

    q.values.forEach(function (value, i) {
      var labelText = qc['opt' + (i + 1)];
      if (!labelText) return;                       // merchant blanked it — drop the option
      var note = qc['opt' + (i + 1) + '_note'];

      var id = 'rf-' + self.cfg.uid + '-' + q.key + '-' + i;
      var wrap = el('label', 'rf__opt');
      wrap.setAttribute('for', id);

      var input = document.createElement('input');
      input.type = q.type === 'multi' ? 'checkbox' : 'radio';
      input.name = 'rf-' + self.cfg.uid + '-' + q.key;
      input.value = value;
      input.id = id;
      input.className = 'rf__input';
      if (Array.isArray(current) ? current.indexOf(value) !== -1 : current === value) {
        input.checked = true;
      }

      var body = el('span', 'rf__opt-body');
      body.appendChild(el('span', 'rf__opt-label', labelText));
      if (note) body.appendChild(el('small', 'rf__opt-note', note));

      /* An empty department is disclosed HERE, before the visitor spends nine
         answers on a track we cannot serve. They may still choose it — the
         advice is worth having either way, and hiding the option would be a
         quieter kind of dishonesty — but they choose it knowing. */
      if (q.key === 'track' && self.unstocked.indexOf(value) !== -1) {
        wrap.classList.add('rf__opt--gap');
        var warn = el('small', 'rf__opt-gap', self.t('ui_track_unstocked'));
        if (warn.textContent) body.appendChild(warn);
      }

      wrap.appendChild(input);
      wrap.appendChild(body);
      list.appendChild(wrap);

      input.addEventListener('change', function () { self.collect(q); });

      // Pointer-only auto-advance. `detail > 0` means a real click; keyboard
      // activation reports 0, and arrow-key navigation never fires click at all.
      wrap.addEventListener('click', function (ev) {
        if (q.type !== 'single') return;
        if (!ev.detail) return;
        window.setTimeout(function () { self.advance(); }, REDUCED ? 0 : 180);
      });
    });

    fs.appendChild(list);
  };

  /* The free-text field. Only ever reachable when an AI endpoint is set — a
     box whose contents nothing reads is a box that wastes the visitor's time
     and collects personal data for no purpose. */
  Finder.prototype.renderTextQuestion = function (fs, q, qc, describedBy) {
    var self = this;
    var id = 'rf-' + this.cfg.uid + '-note';

    var ta = document.createElement('textarea');
    ta.id = id;
    ta.className = 'rf__text';
    ta.rows = 4;
    ta.maxLength = 600;
    ta.name = id;
    if (qc.help) ta.setAttribute('placeholder', '');
    var ph = this.t('ui_note_placeholder');
    if (ph) ta.setAttribute('placeholder', ph);
    ta.value = this.answers[q.key] || '';
    ta.addEventListener('input', function () { self.collect(q); });

    var privacy = this.t('ui_note_privacy');
    if (privacy) {
      var p = el('p', 'rf__privacy', privacy);
      p.id = 'rf-priv-' + this.cfg.uid;
      describedBy.push(p.id);
      fs.appendChild(ta);
      fs.appendChild(p);
    } else {
      fs.appendChild(ta);
    }
  };

  Finder.prototype.collect = function (q) {
    if (q.type === 'text') {
      var ta = this.mount.querySelector('textarea');
      this.answers[q.key] = ta ? ta.value.slice(0, 600) : '';
      this.save();
      return;
    }

    var inputs = this.mount.querySelectorAll('input');
    if (q.type === 'multi') {
      var vals = [];
      Array.prototype.forEach.call(inputs, function (i) { if (i.checked) vals.push(i.value); });

      // "None of these" is exclusive — ticking it clears the rest, and
      // ticking anything else clears it.
      if (q.exclusive && vals.length > 1) {
        var last = this.answers[q.key] || [];
        if (vals.indexOf(q.exclusive) !== -1 && last.indexOf(q.exclusive) === -1) {
          vals = [q.exclusive];
        } else {
          vals = vals.filter(function (v) { return v !== q.exclusive; });
        }
        var set = vals;
        Array.prototype.forEach.call(inputs, function (i) { i.checked = set.indexOf(i.value) !== -1; });
      }
      this.answers[q.key] = vals;
    } else {
      var picked = null;
      Array.prototype.forEach.call(inputs, function (i) { if (i.checked) picked = i.value; });
      this.answers[q.key] = picked;
    }

    // Changing the track answer can invalidate later answers. Drop anything
    // whose question no longer runs, rather than silently scoring it.
    var live = {};
    this.queue().forEach(function (qq) { live[qq.key] = true; });
    for (var k in this.answers) {
      if (this.answers.hasOwnProperty(k) && !live[k]) delete this.answers[k];
    }

    if (this.skip) this.skip.hidden = !E.canResolve(this.answers);
    this.save();
  };

  Finder.prototype.advance = function () {
    var q = this.queue()[this.index];
    if (q) {
      this.collect(q);
      var v = this.answers[q.key];
      var empty = q.type === 'text'
        ? !String(v || '').trim()
        : (v == null || (Array.isArray(v) && v.length < (q.min || 1)));
      if (empty && !q.optional) {
        this.live.textContent = this.t('ui_choose_one');
        var first = this.mount.querySelector('input, textarea');
        if (first) first.focus();
        return;
      }
    }
    this.index++;
    if (this.index >= this.queue().length) this.finish();
    else this.render();
  };

  Finder.prototype.reset = function () {
    this.answers = {};
    this.index = 0;
    this.removed = {};
    this.aiApplied = [];
    this.aiWrote = false;
    try { sessionStorage.removeItem(this.storeKey()); } catch (e) {}
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    this.render();
    this.root.scrollIntoView({ block: 'start', behavior: 'auto' });
  };

  /* -- result --------------------------------------------------------- */

  Finder.prototype.finish = function (quiet) {
    this.aiApplied = [];
    this.aiWrote = false;
    this.rerender(quiet);

    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '',
        window.location.pathname + window.location.search + '#rf2=' + encodeURIComponent(encode(this.answers)));
    }

    // The page is already complete and correct at this point. The AI layer,
    // if configured, can only ever re-run the same deterministic engine with
    // an answer matrix the visitor could have produced themselves.
    this.enhance();
  };

  /* Everything that draws the result. Called again whenever something the
     visitor or the AI layer did changes the inputs — there is exactly one
     rendering path, so an AI-adjusted result cannot drift from a plain one. */
  Finder.prototype.rerender = function (quiet) {
    var res = E.plan(this.answers, this.cat, this.policy);
    this.lastResult = res;

    this.form.hidden = true;
    this.result.hidden = false;
    this.result.textContent = '';
    if (this.intro) this.intro.hidden = true;

    this.renderProfile(res);
    this.renderVerdict(res);
    this.renderGaps(res);

    /* The two modes differ in ORDER as much as in content, and the order is
       the argument.

       Build answers "what should my routine be?", so the routine comes
       first and the advice explains it afterwards.

       Read answers "what is my skin doing, and what should I change?". The
       change is almost always behavioural — stop this, space that out,
       leave the other alone — so the advice comes first and anything for
       sale comes after it, having had to get past the advice to be there at
       all. Put the products first and the page becomes a shopping list with
       a conscience appended; put them second and the reading is the
       product. */
    if (this.cfg.mode === 'profile') {
      this.renderRules(res);
      this.renderHelpers(res);
      this.renderCuts(res);
    } else {
      this.renderRoutine(res);
      this.renderCuts(res);
      this.renderRules(res);
    }

    this.renderTransparency(res);
    this.renderActions(res);

    this.live.textContent = this.t('ui_result_ready');
    if (!quiet) {
      this.result.setAttribute('tabindex', '-1');
      this.result.focus({ preventScroll: true });
      this.result.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  };

  Finder.prototype.renderProfile = function (res) {
    if (res.profile.tracks.indexOf('skin') === -1) return;
    var p = res.profile;
    var card = el('div', 'rf-profile');
    var head = el('div', 'rf-profile__head');
    head.appendChild(el('h2', 'rf-profile__title', this.t('ui_profile_title')));
    head.appendChild(el('p', 'rf-profile__sub', this.t('ui_profile_sub')));
    card.appendChild(head);

    var grid = el('div', 'rf-profile__grid');
    var cells = [
      ['ui_label_type', 'type_' + p.type],
      ['ui_label_barrier', 'barrier_' + p.barrier],
      ['ui_label_sensitivity', 'sens_' + p.sensitivity],
      ['ui_label_focus', 'focus_' + p.focus]
    ];
    for (var i = 0; i < cells.length; i++) {
      var c = el('div', 'rf-profile__cell');
      c.appendChild(el('span', null, this.t(cells[i][0])));
      c.appendChild(el('b', null, this.t(cells[i][1])));
      grid.appendChild(c);
    }
    card.appendChild(grid);
    this.result.appendChild(card);
  };

  Finder.prototype.renderVerdict = function (res) {
    var key = E.verdictKey(res);
    var box = el('div', 'rf-verdict');
    var h = this.t(key + '_heading');
    if (h) box.appendChild(el('h2', 'rf-verdict__h', h));
    paras(box, this.t(key + '_body'));
    box.setAttribute('data-rf-prose', key);
    this.result.appendChild(box);

    // AI Act Art. 50(1), in force since 2 August 2026: the disclosure renders
    // whenever ANY part of what is on screen came from the model — the
    // paragraph, the inferred answers, or both. It is attached here so it
    // cannot be forgotten on a path that only applied signals.
    if (this.aiWrote || this.aiApplied.length) {
      var note = el('p', 'rf-ai-note', this.t('ui_ai_disclosure'));
      if (this.aiApplied.length) {
        var applied = this.t('ui_ai_note_applied');
        if (applied) box.appendChild(el('p', 'rf-ai-note', applied));
      }
      if (note.textContent) box.appendChild(note);
    }
  };

  /* The gap panel.

     31 of 48 products are drafts, and whole departments are empty. Before
     this existed, a visitor who asked for a hair routine was told "we don't
     have anything to sell you today" — which is the brand's most serious
     sentence, reserved for "nothing here is right for you", spent instead on
     a stock-keeping fact. This says the true thing and offers the one useful
     action available: tell me when it lands. */
  Finder.prototype.renderGaps = function (res) {
    if (!res.gaps.length) { if (this.waitlist) this.waitlist.hidden = true; return; }
    var self = this;

    var names = res.gaps.map(function (g) {
      return self.t('slot_' + g.slot.replace(/-/g, '_')) || g.slot;
    }).filter(function (v, i, a) { return a.indexOf(v) === i; });

    var sec = el('section', 'rf-gap');
    var h = this.t('ui_gap_heading');
    if (h) sec.appendChild(el('h3', 'rf-h3', h));
    paras(sec, fill(this.t('ui_gap_body'), { steps: names.join(', ') }));
    this.result.appendChild(sec);

    // The waitlist form is real Liquid rendered by the section — a Shopify
    // form cannot be conjured from JavaScript — so it is moved into place
    // rather than built here.
    if (this.waitlist) {
      this.waitlist.hidden = false;
      var slot = this.waitlist.querySelector('[data-rf-waitlist-subject]');
      if (slot) slot.value = names.join(', ');
      sec.appendChild(this.waitlist);
    }
  };

  Finder.prototype.renderRoutine = function (res) {
    var self = this;
    var kept = res.items.filter(function (it) { return !self.removed[it.product.h]; });

    if (!kept.length) {
      // Only say this when it is true. When the whole result is a stock gap,
      // renderGaps has already said the accurate thing and this would
      // contradict it.
      if (!res.onlyGaps) {
        var none = el('div', 'rf-note');
        none.appendChild(el('strong', null, this.t('ui_nothing_heading')));
        paras(none, this.t('ui_nothing_body'));
        this.result.appendChild(none);
      }
      return;
    }

    var wrap = el('section', 'rf-routine');
    wrap.appendChild(el('h3', 'rf-h3', this.t('ui_your_routine')));

    var ol = el('ol', 'rf-steps');
    kept.forEach(function (it, n) {
      var p = it.product;
      var li = el('li', 'rf-step');

      li.appendChild(el('span', 'rf-step__n', ('0' + (n + 1)).slice(-2)));

      var mid = el('span', 'rf-step__body');
      var a = el('a', 'rf-step__link', (p.v ? p.v + ' ' : '') + p.t);
      a.href = p.u;
      mid.appendChild(a);

      var meta = el('span', 'rf-step__meta',
        [self.t('slot_' + it.slot.replace(/-/g, '_')) || '', variantLabel(p), p.freq || '']
          .filter(Boolean).join(' · '));
      mid.appendChild(meta);

      // Both of these come off the product record rather than out of a
      // translated template, so both go through the claim guard first.
      var why = claimSafe(p.best);
      var skipIf = claimSafe(p.skip);
      if (why) mid.appendChild(el('span', 'rf-step__why', fill(self.t('ui_why_this'), { reason: why })));
      // The product's own "skip if" line, shown on the product we are
      // actively recommending. It is the cheapest possible honesty.
      if (skipIf) mid.appendChild(el('span', 'rf-step__skip', fill(self.t('ui_skip_if'), { reason: skipIf })));

      // We asked to leave something out and cannot yet confirm this product
      // qualifies. Saying so is not a disclaimer, it is the answer.
      if (it.caveats && it.caveats.length) {
        var marker = self.t('ui_caveat_marker');
        if (marker) mid.appendChild(el('span', 'rf-step__caveat', marker));
      }

      li.appendChild(mid);

      var right = el('span', 'rf-step__side');
      right.appendChild(el('span', 'rf-step__price', money(p.p, self.cfg.money)));
      right.appendChild(self.buyControl(p));
      var rm = el('button', 'rf-step__remove', self.t('ui_remove'));
      rm.type = 'button';
      rm.setAttribute('data-rf-remove', p.h);
      rm.setAttribute('aria-label', fill(self.t('ui_remove_aria'), { product: p.t }));
      right.appendChild(rm);
      li.appendChild(right);

      ol.appendChild(li);
    });
    wrap.appendChild(ol);

    var total = kept.reduce(function (n, it) { return n + (it.product.p || 0); }, 0);
    var foot = el('div', 'rf-total');
    foot.appendChild(el('span', null, this.t('ui_total')));
    foot.appendChild(el('b', null, money(total, this.cfg.money)));
    wrap.appendChild(foot);

    /* Add the whole routine in one request.

       Only offered for the products we can actually add without guessing.
       Anything with more than one variant is excluded from the bulk add and
       keeps its own "choose a size" link — putting a 50 ml in someone's
       cart when they wanted the 100 ml is not a rounding error, and it is
       discovered at the door. The button says how many it is adding so the
       difference is visible before the click, not after it. */
    var addable = kept.filter(function (it) { return self.canAdd(it.product); });
    if (addable.length) {
      var bulkTotal = addable.reduce(function (n, it) { return n + (it.product.p || 0); }, 0);
      var bulk = el('button', 'button rf-addall');
      bulk.type = 'button';
      bulk.textContent = fill(this.t('ui_add_all'), {
        count: addable.length,
        total: money(bulkTotal, this.cfg.money)
      });
      bulk.setAttribute('data-rf-addall', '1');
      wrap.appendChild(bulk);

      if (addable.length !== kept.length) {
        var partial = this.t('ui_add_all_partial');
        if (partial) wrap.appendChild(el('p', 'rf-addall__note', partial));
      }

      var status = el('p', 'rf-cart-status');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('data-rf-cart-status', '1');
      wrap.appendChild(status);
    }

    // Anything the visitor removed, with a way back.
    var dropped = res.items.filter(function (it) { return self.removed[it.product.h]; });
    if (dropped.length) {
      var back = el('p', 'rf-undo');
      back.appendChild(document.createTextNode(this.t('ui_removed') + ' '));
      dropped.forEach(function (it, i) {
        if (i) back.appendChild(document.createTextNode(', '));
        var b = el('button', 'rf-undo__btn', it.product.t);
        b.type = 'button';
        b.setAttribute('data-rf-undo', it.product.h);
        back.appendChild(b);
      });
      wrap.appendChild(back);
    }

    this.result.appendChild(wrap);

    // One explanation per distinct caveat, under the list, rather than the
    // same sentence repeated five times beside five products.
    var live = [];
    kept.forEach(function (it) {
      (it.caveats || []).forEach(function (k) { if (live.indexOf(k) === -1) live.push(k); });
    });
    if (live.length) {
      var sec = el('section', 'rf-caveats');
      var h = this.t('ui_caveat_heading');
      if (h) sec.appendChild(el('h3', 'rf-h3', h));
      var ul = el('ul', 'rf-caveats__list');
      live.forEach(function (k) {
        var text = self.t('caveat_' + k);
        if (text) ul.appendChild(el('li', null, text));
      });
      if (ul.childNodes.length) { sec.appendChild(ul); this.result.appendChild(sec); }
    }
  };

  /* ------------------------------------------------------------------ */
  /* buying                                                              */
  /* ------------------------------------------------------------------ */

  /* Can this product be put in a cart without us choosing something on the
     visitor's behalf? Three ways the answer is no, and all three are real
     in this catalogue rather than defensive programming:

       - no variant id reached the page (a Liquid change, or a product with
         nothing purchasable on it at all)
       - the product is not available
       - the product has more than one variant, so "add" would mean picking
         a size for someone who has not seen the sizes

     A `false` here is not an error state. It is a product that gets a link
     to its own page instead of a button, which is the correct outcome. */
  Finder.prototype.canAdd = function (p) {
    return !!(p && p.vid && p.av !== false && (p.nv == null || p.nv === 1));
  };

  /* The lines the "add all" button is actually going to send. Recomputed
     from the current result at click time rather than captured when the
     button was drawn, so anything the visitor removed in between is
     genuinely gone from the request and not merely hidden. */
  Finder.prototype.addableLines = function () {
    var self = this;
    var res = this.lastResult;
    if (!res) return [];
    return res.items
      .filter(function (it) { return !self.removed[it.product.h] && self.canAdd(it.product); })
      .map(function (it) { return { id: it.product.vid, quantity: 1 }; });
  };

  Finder.prototype.buyControl = function (p) {
    if (!this.canAdd(p)) {
      /* Two different facts, two different labels. "Pick a size" is a choice
         still open to the visitor; anything else is a product we cannot sell
         them from here. Order matters: check the variant count FIRST, because
         a product with no usable variant id is not "choose a size" — sending
         someone to pick an option that isn't there is worse than saying the
         product is unavailable and linking to it. */
      var many = p && p.nv > 1;
      var label = many ? this.t('ui_choose_variant') : this.t('ui_sold_out');
      var a = el('a', 'rf-step__choose', label);
      a.href = p.u;
      return a;
    }
    var b = el('button', 'rf-step__add', this.t('ui_add'));
    b.type = 'button';
    b.setAttribute('data-rf-add', String(p.vid));
    // The visible label is one word by design; the accessible name has to
    // say which of eight buttons this is.
    b.setAttribute('aria-label', fill(this.t('ui_add_aria'), { product: p.t }));
    return b;
  };

  /* One place where a cart request becomes something on screen, so a single
     add and a whole-routine add cannot report differently. */
  Finder.prototype.runAdd = function (lines, button) {
    var self = this;

    /* Nothing to send. Reachable if the visitor removes every addable step
       between the button being drawn and being pressed. `/cart/add.js` with
       an empty items array returns 200, so without this guard the page would
       confirm an add that never happened — the exact failure class this
       project keeps a file about. */
    if (!lines || !lines.length) return Promise.resolve();

    var status = this.result.querySelector('[data-rf-cart-status]');
    var was = button ? button.textContent : '';

    function say(text) {
      if (status) status.textContent = text || '';
      // The live region is the one a screen reader is already watching.
      if (text) self.live.textContent = text;
    }

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      var busy = this.t('ui_adding');
      if (busy) button.textContent = busy;
    }
    say('');

    return cartAdd(lines)
      .then(function (data) {
        say(fill(self.t('ui_added'), { count: lines.length }));
        cartReveal(data, button);
      })
      .catch(function (e) {
        // Two failures, two sentences. "Sold out while you were reading" and
        // "your connection dropped" are not the same problem and the visitor
        // can act on exactly one of them.
        say(self.t(e && e.code === 'invalid' ? 'ui_cart_unavailable' : 'ui_cart_error'));
      })
      .then(function () {
        if (!button) return;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = was;
      });
  };

  /* ------------------------------------------------------------------ */
  /* read mode — the shortlist, if there is one                          */
  /*                                                                     */
  /* This is the panel that changed what the read tool is. It used to end */
  /* with a reading and no way to act on it; it now ends with at most two */
  /* products, each carrying the specific reason it is there, and it ends */
  /* with none at all whenever the honest answer is that nothing we sell  */
  /* would improve on what the visitor is already doing.                  */
  /*                                                                     */
  /* The empty case is rendered as loudly as the full one, on purpose. A  */
  /* tool that can only ever say "buy these two" is a vending machine     */
  /* with a quiz on the front.                                            */
  /* ------------------------------------------------------------------ */

  Finder.prototype.renderHelpers = function (res) {
    var self = this;
    var list = (res.helpers || []).filter(function (h) { return !self.removed[h.product.h]; });

    var sec = el('section', 'rf-helpers');

    if (!list.length) {
      // Do not claim "nothing would help you" when the truth is "we have
      // not started stocking that shelf". renderGaps owns that sentence.
      if (res.onlyGaps) return;
      var h0 = this.t('ui_helpers_none_heading');
      if (h0) sec.appendChild(el('h3', 'rf-h3', h0));
      paras(sec, this.t('ui_helpers_none_body'));
      if (sec.childNodes.length) this.result.appendChild(sec);
      return;
    }

    var h = this.t('ui_helpers_heading');
    if (h) sec.appendChild(el('h3', 'rf-h3', h));
    paras(sec, this.t('ui_helpers_intro'), 'rf-helpers__intro');

    var ul = el('ul', 'rf-helpers__list');
    list.forEach(function (it) {
      var p = it.product;
      var li = el('li', 'rf-helper');

      // The reason comes first and the product second. That ordering is the
      // whole argument: this is advice with a product attached, not a
      // product with a justification attached.
      var reason = self.t(it.why + '_body');
      if (reason) li.appendChild(el('p', 'rf-helper__why', reason));

      var row = el('div', 'rf-helper__row');
      var a = el('a', 'rf-helper__link', (p.v ? p.v + ' ' : '') + p.t);
      a.href = p.u;
      row.appendChild(a);

      var vn = variantLabel(p);
      if (vn) row.appendChild(el('span', 'rf-helper__variant', vn));
      row.appendChild(el('span', 'rf-helper__price', money(p.p, self.cfg.money)));
      row.appendChild(self.buyControl(p));
      li.appendChild(row);

      var skipIf = claimSafe(p.skip);
      if (skipIf) li.appendChild(el('p', 'rf-helper__skip', fill(self.t('ui_skip_if'), { reason: skipIf })));

      if (it.caveats && it.caveats.length) {
        var marker = self.t('ui_caveat_marker');
        if (marker) li.appendChild(el('p', 'rf-step__caveat', marker));
      }

      ul.appendChild(li);
    });
    sec.appendChild(ul);

    var status = el('p', 'rf-cart-status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('data-rf-cart-status', '1');
    sec.appendChild(status);

    this.result.appendChild(sec);

    // Same one-explanation-per-caveat treatment the routine gets.
    var live = [];
    list.forEach(function (it) {
      (it.caveats || []).forEach(function (k) { if (live.indexOf(k) === -1) live.push(k); });
    });
    if (live.length) {
      var cav = el('section', 'rf-caveats');
      var ch = this.t('ui_caveat_heading');
      if (ch) cav.appendChild(el('h3', 'rf-h3', ch));
      var cul = el('ul', 'rf-caveats__list');
      live.forEach(function (k) {
        var text = self.t('caveat_' + k);
        if (text) cul.appendChild(el('li', null, text));
      });
      if (cul.childNodes.length) { cav.appendChild(cul); this.result.appendChild(cav); }
    }
  };

  /* The panel the shop exists to be able to show. */
  Finder.prototype.renderCuts = function (res) {
    if (!res.cuts.length) return;
    var self = this;
    var sec = el('section', 'rf-cuts');
    sec.appendChild(el('h3', 'rf-h3', this.t('ui_left_out')));
    var ul = el('ul', 'rf-cuts__list');
    var seen = {};
    res.cuts.forEach(function (cut) {
      // Stock gaps have their own panel and their own honest sentence. Listing
      // them here as well would read as us finding fault with the visitor.
      if (cut.reason === 'not_stocked_yet') return;
      var text = self.t('cut_' + cut.reason);
      if (!text || seen[cut.reason + cut.slot]) return;
      seen[cut.reason + cut.slot] = true;
      var li = el('li');
      li.appendChild(el('strong', null, self.t('slot_' + cut.slot.replace(/-/g, '_')) || cut.slot));
      li.appendChild(document.createTextNode(' — ' + text));
      ul.appendChild(li);
    });
    if (ul.childNodes.length) { sec.appendChild(ul); this.result.appendChild(sec); }
  };

  Finder.prototype.renderRules = function (res) {
    if (!res.rules.length) return;
    var self = this;
    var sec = el('section', 'rf-rules');
    sec.appendChild(el('h3', 'rf-h3', this.t('ui_hold_off')));
    var ul = el('ul', 'rf-rules__list');
    res.rules.forEach(function (key) {
      var title = self.t(key + '_title');
      var body = self.t(key + '_body');
      if (!title && !body) return;
      var li = el('li');
      if (title) li.appendChild(el('strong', null, title));
      if (body) li.appendChild(document.createTextNode(' ' + body));
      ul.appendChild(li);
    });
    if (ul.childNodes.length) { sec.appendChild(ul); this.result.appendChild(sec); }
  };

  /* UCPD Art. 7(4a): where a trader presents products in response to a
     consumer query, the main ranking parameters must be disclosed. A quiz
     result is exactly that, so we say plainly how the order was decided. */
  Finder.prototype.renderTransparency = function (res) {
    var d = el('details', 'rf-how');
    var s = el('summary', null, this.t('ui_how_we_chose'));
    d.appendChild(s);
    paras(d, this.t('ui_how_we_chose_body'));
    var ul = el('ul', 'rf-how__list');
    (this.copy.ui_ranking_factors || '').split('\n').forEach(function (line) {
      var t = line.trim();
      if (t) ul.appendChild(el('li', null, t));
    });
    if (ul.childNodes.length) d.appendChild(ul);
    this.result.appendChild(d);
  };

  Finder.prototype.renderActions = function (res) {
    var acts = el('div', 'rf-acts');

    if (this.cfg.shopUrl && res.items.length) {
      var a = el('a', 'button', this.t('ui_shop_cta'));
      a.href = this.cfg.shopUrl;
      acts.appendChild(a);
    }
    var again = el('button', 'button button--secondary', this.t('ui_restart'));
    again.type = 'button';
    again.setAttribute('data-rf-restart', '1');
    acts.appendChild(again);

    this.result.appendChild(acts);

    var safe = el('p', 'rf-safety');
    paras(safe, this.t('ui_safety'));
    this.result.appendChild(safe);
  };

  /* ------------------------------------------------------------------ */
  /* waitlist                                                            */
  /*                                                                     */
  /* A real Shopify contact form, so it works with JavaScript disabled    */
  /* and needs no app, no list and no third party. The fetch path below   */
  /* only exists to avoid throwing away the result page on submit; if it  */
  /* fails for any reason the native submit takes over.                   */
  /* ------------------------------------------------------------------ */

  Finder.prototype.bindWaitlist = function () {
    var self = this;
    var form = this.waitlist.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', function (ev) {
      if (!window.fetch || !window.FormData) return;      // let the browser do it
      ev.preventDefault();
      var status = self.waitlist.querySelector('[data-rf-waitlist-status]');
      var button = form.querySelector('button[type="submit"]');
      if (button) button.disabled = true;

      fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('bad status');
          form.hidden = true;
          if (status) { status.textContent = self.t('ui_waitlist_thanks'); status.hidden = false; }
        })
        .catch(function () {
          if (button) button.disabled = false;
          if (status) { status.textContent = self.t('ui_waitlist_error'); status.hidden = false; }
        });
    });
  };

  /* ------------------------------------------------------------------ */
  /* optional AI layer                                                   */
  /*                                                                     */
  /* The deterministic result is already rendered and complete before     */
  /* this runs. Two things can come back:                                 */
  /*                                                                      */
  /*   verdict  — prose, which replaces prose already on screen.          */
  /*   signals  — proposed ANSWERS, which are validated against the       */
  /*              engine's own vocabulary, clamped to the conservative    */
  /*              direction, and then fed back through the same           */
  /*              deterministic planner. The model never sees the         */
  /*              catalogue and never names a product.                     */
  /*                                                                      */
  /* If it fails, is slow, or returns anything unexpected, the page is    */
  /* untouched — the visitor never sees an error because there is         */
  /* nothing broken to report.                                            */
  /* ------------------------------------------------------------------ */

  Finder.prototype.enhance = function () {
    var cfg = this.cfg.ai;
    if (!cfg || !cfg.endpoint) return;
    if (!window.fetch || !window.AbortController) return;

    var self = this;
    var res = this.lastResult;
    var ctrl = new AbortController();
    var done = false;
    var timer = window.setTimeout(function () { if (!done) ctrl.abort(); }, cfg.timeout || 6000);

    // REDACTION BOUNDARY — do not widen without a DPIA.
    //
    // The engine's profile carries `shelf`, which includes "a prescription
    // cream from a doctor". That answer reveals the visitor is under medical
    // care and is the one field in this tool that is plainly Art. 9 GDPR
    // health data. It changes the recommendation locally and is never
    // transmitted. The same goes for the raw answer matrix and for product
    // handles, prices and URLs — the model has no use for any of them and
    // cannot name a product it was never given.
    var p = res.profile;
    var payload = {
      locale: this.cfg.locale,
      profile: {
        tracks: p.tracks, type: p.type, barrier: p.barrier,
        sensitivity: p.sensitivity, focus: p.focus,
        focusOverridden: p.focusOverridden, stage: p.stage,
        budget: p.budget, sun: p.sun, avoid: p.avoid,
        body: p.body, scalp: p.scalp, hair: p.hair, scent: p.scent
      },
      slots: res.items.map(function (i) { return { slot: i.slot, title: i.product.t }; }),
      cuts: res.cuts.map(function (c) { return { slot: c.slot, reason: c.reason }; }),
      gaps: res.gaps.map(function (g) { return g.slot; }),
      rules: res.rules.filter(function (r) { return r !== 'rule_prescription'; }),
      note: this.answers.note ? String(this.answers.note).slice(0, 600) : ''
    };

    fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: ctrl.signal,
      credentials: 'omit'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        done = true;
        window.clearTimeout(timer);
        if (!data || typeof data !== 'object') return;

        // 1. Signals first: they can change the products, so they must be
        //    applied before any prose is written onto the page.
        var merged = E.applySignals(self.answers, data.signals);
        if (merged.applied.length) {
          self.answers = merged.answers;
          self.aiApplied = merged.applied;
        }

        var verdict = typeof data.verdict === 'string' ? data.verdict.trim() : '';
        self.aiWrote = !!verdict;

        // 2. One render path. An AI-adjusted result is drawn by exactly the
        //    same code as a plain one, so the two cannot diverge.
        if (merged.applied.length) self.rerender(true);

        if (verdict) {
          var box = self.result.querySelector('[data-rf-prose]');
          if (!box) return;
          var keep = box.querySelector('.rf-verdict__h');
          box.textContent = '';
          if (keep) box.appendChild(keep);
          paras(box, verdict);
          if (self.aiApplied.length) {
            var applied = self.t('ui_ai_note_applied');
            if (applied) box.appendChild(el('p', 'rf-ai-note', applied));
          }
          var tag = el('p', 'rf-ai-note', self.t('ui_ai_disclosure'));
          if (tag.textContent) box.appendChild(tag);
        }
      })
      .catch(function () { done = true; window.clearTimeout(timer); });
  };

  /* ------------------------------------------------------------------ */

  function boot() {
    document.querySelectorAll('[data-rf-root]').forEach(function (root) {
      if (root.dataset.rfBooted) return;
      root.dataset.rfBooted = '1';
      // The engine is a separate asset and a separate request. If it 404s or
      // is blocked, the shell would otherwise sit there empty and silent
      // forever — the one failure mode the old boot guard did not cover,
      // because it only caught exceptions from a Finder that had started.
      if (!E) { root.setAttribute('data-rf-failed', '1'); return; }
      try { new Finder(root); } catch (e) {
        // A broken finder must not take the page with it. Fall back to the
        // no-JS content that is already in the markup.
        root.setAttribute('data-rf-failed', '1');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
